import { css } from '@emotion/react';
import { t } from 'i18n';
import { DateTime } from 'luxon';
import type { BoundFlight, Segment, SegmentVia, Via } from 'shared';

import { getFlightStopText } from '../../utils';
import { SasIcon } from '../SasIcon';
import { theme } from '../theme';
import { ToolTip } from '../ToolTip';

const styles = {
  flightDetailDuration: css`
    background: var(--color-blue-2);
    color: #fff;
    padding: 5px 0 5px 20px;
    font-weight: 400;
    font-size: ${theme.fontSize.sm};
    display: flex;
    vertical-align: center;
    align-items: center;
  `,
  segmentHaltInfo: css`
    margin: 10px 0 10px 0;
  `,
  flightSegmentDetail: (isVia: boolean) => css`
    display: flex;
    background: ${isVia ? 'var(--g0-color)' : 'var(--color-white)'};
  `,
  flightSegmentDetailDuration: css`
    flex-flow: column nowrap;
    justify-content: center;
    align-items: center;
    display: flex;
    margin-left: 20px;
    position: relative;
  `,
  durationHalt: (position: 'top' | 'bottom') => css`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: var(--color-blue-2);
    position: absolute;
    top: ${position === 'top' ? '0' : 'auto'};
    bottom: ${position === 'bottom' ? '0' : 'auto'};
  `,
  durationVertical: (addTopSpacing: boolean) => css`
    width: 6px;
    height: 1px;
    background-color: var(--color-blue-2);
    position: absolute;
    top: ${addTopSpacing ? '10px' : 'auto'};
    bottom: 10px;
  `,
  flightDurationBar: (isSegment: boolean, isFirst: boolean, isLast: boolean) => css`
    align-items: center;
    justify-content: center;
    padding: 15px 0;
    background-clip: content-box;
    width: 2px;
    background: ${isSegment ? 'var(--brand-color)' : 'var(--g1-color)'};
    border-top: ${!isFirst ? '10px solid var(--g1-color)' : 'none'};
    border-bottom: ${!isLast ? '10px solid var(--g1-color)' : 'none'};
    height: 100%;
  `,
  flightSegmentLocationDetail: css`
    padding: 15px 0 15px 20px;
  `,
  haltDetails: css`
    background: var(--g0-color);
  `,
  locationDetails: css`
    font-size: ${theme.fontSize.sm};
    margin: 0;
  `,
  depatureDetails: css`
    font-size: ${theme.fontSize.sm};
  `,
  flightTime: css`
    font-size: ${theme.fontSize.sm};
  `,
  flightDetails: css`
    margin-top: 10px;
  `,
  arrivalDetails: css`
    margin-top: 10px;
  `,
  airportCode: css`
    font-weight: 800;
  `,
  timeDuration: css`
    font-weight: 800;
  `,
  flightNumber: css`
    margin: 0;
    font-size: ${theme.fontSize.sm};
  `,
  operatingCarrier: css`
    margin: 0;
    font-size: ${theme.fontSize.sm};
  `,
  locationHaltDetails: css`
    font-size: ${theme.fontSize.sm};
    margin: 0;
  `,
  locationHopperDetails: css`
    font-size: ${theme.fontSize.sm};
    padding: 2px 5px 2px 5px;
    border-radius: 20px;
    background-color: var(--color-white);
    display: inline-block;
    margin: 10px 0 0 -5px;
  `,
  segmentHaltDetails: css`
    background: var(--g0-color);
    margin-top: 10px;
    padding: 10px 0 10px 42px;
    margin-left: -42px;
  `,
  flightTotalDuration: css`
    margin-left: 5px;
  `,
  terminalDetails: css`
    font-size: ${theme.fontSize.sm};
    color: #3f3d3a;
  `,
  haltInformation: css`
    font-size: ${theme.fontSize.sm};
    font-weight: 800;
    margin-top: 5px;
  `,
};

interface FlightInfoProps {
  open: boolean;
  flight: BoundFlight;
  close: (e: React.MouseEvent<Element>) => void;
}

export const FlightRowDetailsPopup = ({ open, flight, close }: FlightInfoProps) => {
  const flightData = buildFlightData(flight);

  return (
    <ToolTip
      open={open}
      tipPosition={`topLeft`}
      heading={t('common:flightDetails')}
      width={320}
      placement={{ top: '10px', bottom: 'auto', right: 'auto', left: '40px' }}
      backDrop={true}
      handleClose={e => close(e)}
    >
      <div>
        <div css={styles.flightDetailDuration}>
          <SasIcon color={theme.colors.white} name="cl--plane-right" />
          <div css={styles.flightTotalDuration}>
            {flightData.startTimeInLocal} - {flightData.endTimeInLocal} (
            {flightData.stops > 0 ? `${getFlightStopText(flightData.stops)}, ` : ``}
            {flightData.totalDuration})
          </div>
        </div>
        <div css={styles.segmentHaltInfo}>
          {flightData.segmentData.map((segData, idx) => {
            return (
              <FlightSegmentDetails key={segData.id} segData={segData} nextSegData={flightData.segmentData[idx + 1]} />
            );
          })}
        </div>
      </div>
    </ToolTip>
  );
};

type FlightSegmentDetailsProps = {
  segData: SegmentData;
  nextSegData?: SegmentData;
};

const FlightSegmentDetails = ({ segData, nextSegData }: FlightSegmentDetailsProps) => {
  const { type, isFirst, isLast } = segData;
  const isSegment = type === 'Segment';
  const isVia = type === 'Via';

  return (
    <div css={styles.flightSegmentDetail(isVia)}>
      <div css={styles.flightSegmentDetailDuration}>
        {isSegment && isFirst && <span css={styles.durationHalt('top')} />}
        {isSegment && !isFirst && <span css={styles.durationVertical(true)} />}

        <div css={styles.flightDurationBar(isSegment, isFirst, isLast)} />

        {isSegment && isLast && <span css={styles.durationHalt('bottom')} />}
        {isSegment && !isLast && <span css={styles.durationVertical(false)} />}
      </div>

      <div css={styles.flightSegmentLocationDetail}>
        {isSegment ? <SegmentDetails segData={segData} /> : <ViaDetails segData={segData} nextSegData={nextSegData} />}
      </div>
    </div>
  );
};

const ViaDetails = ({ segData, nextSegData }: { segData: SegmentData; nextSegData?: SegmentData }) => {
  const isDifferentDepartureAirport =
    nextSegData && nextSegData.location?.depAirportCode !== segData.location?.arrAirportCode;
  return (
    <div css={styles.haltDetails}>
      {segData.location ? (
        <p css={styles.locationDetails}>
          {isDifferentDepartureAirport ? (
            <>
              {t('notice.changeAirport')} {nextSegData?.location?.depAirportName},{' '}
              <strong css={styles.airportCode}> {nextSegData?.location?.depAirportCode}</strong>
            </>
          ) : (
            <>
              {segData.location.arrAirportName}, {segData.location.arrCityName}
              <strong css={styles.airportCode}> {segData.location.arrAirportCode}</strong>
            </>
          )}
        </p>
      ) : null}

      <div css={styles.locationHaltDetails}>
        {t('common:timeBetweenFlights')}:<strong css={styles.timeDuration}> {segData.duration}</strong>
      </div>
    </div>
  );
};

const SegmentDetails = ({ segData }: { segData: SegmentData }) => {
  return (
    <>
      <div css={styles.depatureDetails}>
        <span css={styles.flightTime}>
          <strong css={styles.timeDuration}>{segData.startDateTime}</strong> ({segData.duration})
        </span>
        {segData.location ? (
          <div css={styles.locationDetails}>
            {segData.location.depAirportName} {segData.location.depCityName}
            <div css={styles.airportCode}>{segData.location.depAirportCode}</div>
          </div>
        ) : null}
        {segData.departureTerminal ? (
          <div css={styles.terminalDetails}>
            {t('common:departureTerminal', { terminal: segData.departureTerminal })}
          </div>
        ) : null}
      </div>

      <div css={styles.flightDetails}>
        <p css={styles.flightNumber}>
          {segData.flightNumber} {segData.aircraft ? `| ${segData.aircraft}` : ``}
        </p>
        <p css={styles.operatingCarrier}>{t('operatedBy', { airline: segData.operatedBy })}</p>
      </div>

      {segData.segmentVia
        ? segData.segmentVia.map(segVia => {
            return (
              <div css={styles.segmentHaltDetails} key={segVia.code}>
                <div css={styles.locationHaltDetails}>
                  <span>
                    {segVia.name}
                    <div css={styles.airportCode}>{segVia.code}</div>
                  </span>
                  <p css={styles.haltInformation}>{t('noAircraftChange')}</p>
                </div>

                <p css={styles.locationHopperDetails}>
                  {t('common:stoppingTime')}: {segVia.duration}
                </p>
              </div>
            );
          })
        : null}

      <div css={styles.arrivalDetails}>
        <span css={styles.flightTime}>
          <strong css={styles.timeDuration}>{segData.endDateTime}</strong>
        </span>
        {segData.location ? (
          <div css={styles.locationDetails}>
            {segData.location.arrAirportName} {segData.location.arrCityName}
            <div css={styles.airportCode}>{segData.location.arrAirportCode}</div>
          </div>
        ) : null}
        {segData.arrivalTerminal && (
          <div css={styles.terminalDetails}>{t('common:arrivalTerminal', { terminal: segData.arrivalTerminal })}</div>
        )}
      </div>
    </>
  );
};

interface FlightData {
  stops: number;
  totalDuration: string;
  startTimeInLocal: string;
  endTimeInLocal: string;
  origin: string;
  destination: string;
  segmentData: SegmentData[];
}

interface SegmentData {
  aircraft?: string;
  startDateTime?: string;
  endDateTime?: string;
  origin: string;
  destination: string;
  isHopper?: boolean;
  isFirst: boolean;
  isLast: boolean;
  type: string;
  showToolTip: boolean;
  id: string;
  flightNumber?: string;
  duration: string;
  location?: Partial<Record<Location, string>>;
  segmentVia?: { code: string; duration: string; name: string }[];
  operatedBy?: string;
  arrivalTerminal?: string;
  departureTerminal?: string;
}

type Location =
  | 'depAirportCode'
  | 'depAirportName'
  | 'depCityName'
  | 'arrAirportCode'
  | 'arrAirportName'
  | 'arrCityName';

function buildFlightData(flight: BoundFlight) {
  const { startTimeInLocal, endTimeInLocal } = flight;
  const _lastSegIndex = flight.segments.length - 1;
  const segmentData = flight.segments.reduce((_segmentData: SegmentData[], segment: Segment) => {
    const segIndex = flight.segments.indexOf(segment);
    const { code: depAirportCode, name: depAirportName } = segment.departureAirport;
    const { name: depCityName } = segment.departureCity;
    const { code: arrAirportCode, name: arrAirportName } = segment.arrivalAirport;
    const { name: arrCityName } = segment.arrivalCity;
    let segmentVia;
    if (segment.via) {
      segmentVia = segment.via.map((segVia: SegmentVia) => {
        return {
          code: segVia.code,
          duration: segVia.haltDuration,
          name: segVia.name,
        };
      });
    }

    _segmentData.push({
      startDateTime: DateTime.fromISO(segment.startTimeInLocal).toFormat('HH:mm'),
      endDateTime: DateTime.fromISO(segment.endTimeInLocal).toFormat('HH:mm'),
      departureTerminal: segment.departureTerminal,
      arrivalTerminal: segment.arrivalTerminal,
      aircraft: segment.airCraft.name,
      origin: segment.departureAirport.code,
      destination: segment.arrivalAirport.code,
      isFirst: segIndex === 0,
      isLast: segIndex === _lastSegIndex,
      type: 'Segment',
      showToolTip: false,
      id: `${flight.id}-${segment.departureAirport.code}-Segment`,
      flightNumber: `${segment.marketingCarrier.code || segment.operatingCarrier.code} ${segment.flightNumber}`,
      operatedBy: `${segment.marketingCarrier.name}` || `${segment.operatingCarrier.name}`,
      duration: segment.duration,
      location: {
        depAirportCode,
        depAirportName,
        depCityName,
        arrAirportCode,
        arrAirportName,
        arrCityName,
      },
      ...(segmentVia && { segmentVia }),
    });
    if (flight.via) {
      const _lastViaIndex = flight.via.length - 1;
      const associatedSegmentVia = flight.via.find((_via: Via) => _via.code === segment.arrivalAirport.code);
      if (associatedSegmentVia) {
        const viaIndex = flight.via.indexOf(associatedSegmentVia);
        _segmentData.push({
          origin: associatedSegmentVia.code,
          destination: associatedSegmentVia.code,
          isFirst: viaIndex === 0,
          isLast: viaIndex === _lastViaIndex,
          type: 'Via',
          showToolTip: false,
          id: `${flight.id}-${associatedSegmentVia.code}-Via`,
          duration: associatedSegmentVia.haltDuration,
          location: {
            arrAirportCode,
            arrAirportName,
            arrCityName,
            depAirportCode: associatedSegmentVia.code,
            depAirportName,
            depCityName,
          },
        });
      }
    }
    return _segmentData;
  }, []);

  return {
    stops: flight.stops,
    totalDuration: flight.connectionDuration,
    startTimeInLocal,
    endTimeInLocal,
    destination: flight.destination.code,
    origin: flight.origin.code,
    segmentData,
  } as FlightData;
}
